import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Building2, Camera, MapPinned, Home as HomeIcon, Activity, DoorOpen, CheckCircle2, ServerCog, ArrowRight } from 'lucide-react';
import { CTAButton, Pill, SectionLabel, fadeUp } from '../components/shared';
import { SocialProof } from '../components/SocialProof';
import { services, industries, processSteps } from '../data/site';
export function Home() {
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 pt-20 text-white sm:pt-28" data-id="element-298">
        <div className="absolute left-1/2 top-0 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-sky-500/20 blur-3xl" data-id="element-299" />
        <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-blue-700/20 blur-3xl" data-id="element-300" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)',
        backgroundSize: '42px 42px'
      }} data-id="element-301" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-10 sm:gap-12 sm:pb-24 sm:pt-16 sm:px-6 lg:grid-cols-[1.02fr_.98fr] lg:px-8 lg:pb-32 lg:pt-24" data-id="element-302">
          <motion.div initial="hidden" animate="visible" transition={{
          staggerChildren: 0.09
        }} data-id="element-303">
            <motion.div variants={fadeUp} data-id="element-304">
              <SectionLabel dark data-id="element-305">Commercial first security</SectionLabel>
            </motion.div>
            <motion.h1 variants={fadeUp} className="max-w-4xl text-4xl font-black leading-[1] tracking-tight sm:text-6xl lg:text-7xl" data-id="element-306">
              See More. Control More. Worry Less.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 max-w-2xl text-base leading-7 text-slate-300 sm:mt-7 sm:text-lg sm:leading-8 lg:text-xl" data-id="element-307">
              Watchdog Security designs, installs, repairs, and supports smart
              alarm, camera, access control, and automation systems for
              businesses, properties, and homes across Northern California.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-7 flex flex-col gap-3 sm:mt-9 sm:flex-row" data-id="element-308">
              <CTAButton to="/assessment" data-id="element-309">
                Schedule a Free Security Assessment
              </CTAButton>
              <CTAButton variant="secondary" to="/commercial" data-id="element-310">
                Explore Commercial Solutions
              </CTAButton>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-8 hidden flex-wrap gap-3 lg:flex" data-id="element-311">
              <Pill data-id="element-312">Access Control</Pill>
              <Pill data-id="element-313">Video Surveillance</Pill>
              <Pill data-id="element-314">Intrusion Alarms</Pill>
              <Pill data-id="element-315">Alarm.com Powered</Pill>
              <Pill data-id="element-316">Local Installation</Pill>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 24
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative" data-id="element-317">
            <div className="absolute -inset-4 rounded-[2rem] bg-sky-400/10 blur-2xl" data-id="element-318" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] p-3 shadow-2xl backdrop-blur-xl sm:p-4" data-id="element-319">
              <div className="rounded-[1.25rem] border border-white/10 bg-slate-900 p-4 sm:rounded-[1.5rem] sm:p-5" data-id="element-320">
                <div className="mb-4 flex items-center justify-between sm:mb-5" data-id="element-321">
                  <div data-id="element-322">
                    <p className="text-sm font-bold text-sky-200" data-id="element-323">
                      Watchdog Command Center
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-324">
                      Live property overview
                    </p>
                  </div>
                  <div className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-[10px] font-bold text-emerald-300 sm:px-3 sm:text-xs" data-id="element-325">
                    All Active
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-326">
                  {/* Live Cameras */}
                  <div className="rounded-2xl border border-white/10 bg-black/30 p-3 sm:rounded-3xl sm:p-4" data-id="element-327">
                    <div className="mb-3 flex items-center justify-between sm:mb-4" data-id="element-328">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-white sm:text-sm" data-id="element-329">
                        <Camera className="h-3.5 w-3.5 text-sky-300 sm:h-4 sm:w-4" data-id="element-330" />
                        Cameras
                      </div>
                      <span className="text-[10px] text-slate-400 sm:text-xs" data-id="element-331">
                        12 on
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2" data-id="element-332">
                      {['Front', 'Patio', 'Back', 'Lot'].map((item, index) => <div key={item} className="relative h-12 overflow-hidden rounded-xl bg-gradient-to-br from-slate-700 to-slate-950 sm:h-20 sm:rounded-2xl" data-id="element-333">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,rgba(56,189,248,.45),transparent_32%)]" data-id="element-334" />
                          <div className="absolute bottom-1 left-1 rounded-full bg-black/50 px-1.5 py-0.5 text-[9px] font-bold text-white sm:bottom-2 sm:left-2 sm:px-2 sm:py-1 sm:text-[10px]" data-id="element-335">
                            {item}
                          </div>
                          {index === 0 && <div className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-red-400 sm:right-2 sm:top-2 sm:h-2 sm:w-2" data-id="element-336" />}
                        </div>)}
                    </div>
                  </div>

                  {/* Door Access */}
                  <div className="rounded-2xl border border-white/10 bg-black/30 p-3 sm:rounded-3xl sm:p-4" data-id="element-337">
                    <div className="mb-3 flex items-center gap-1.5 text-xs font-bold text-white sm:mb-4 sm:text-sm" data-id="element-338">
                      <DoorOpen className="h-3.5 w-3.5 text-sky-300 sm:h-4 sm:w-4" data-id="element-339" />
                      Doors
                    </div>
                    <div className="space-y-1.5 sm:space-y-3" data-id="element-340">
                      {['Front Entry', 'Office', 'Back Door', 'Storage'].map((door, i) => <div key={door} className="flex items-center justify-between rounded-lg bg-white/5 px-2 py-1.5 sm:rounded-2xl sm:px-3 sm:py-3" data-id="element-341">
                            <span className="text-[10px] text-slate-300 sm:text-xs" data-id="element-342">
                              {door}
                            </span>
                            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold sm:px-2 sm:py-1 sm:text-[10px] ${i === 2 ? 'bg-amber-400/15 text-amber-200' : 'bg-emerald-400/15 text-emerald-300'}`} data-id="element-343">
                              {i === 2 ? 'Vendor' : 'Locked'}
                            </span>
                          </div>)}
                    </div>
                  </div>

                  {/* Activity Alerts */}
                  <div className="rounded-2xl border border-white/10 bg-black/30 p-3 sm:rounded-3xl sm:p-4" data-id="element-344">
                    <div className="mb-3 flex items-center gap-1.5 text-xs font-bold text-white sm:mb-4 sm:text-sm" data-id="element-345">
                      <Activity className="h-3.5 w-3.5 text-sky-300 sm:h-4 sm:w-4" data-id="element-346" />
                      Activity
                    </div>
                    <div className="space-y-1.5 text-[10px] text-slate-300 sm:space-y-3 sm:text-xs" data-id="element-347">
                      <p className="rounded-lg bg-white/5 p-2 sm:rounded-2xl sm:p-3" data-id="element-348">
                        8:02 • Armed
                      </p>
                      <p className="rounded-lg bg-white/5 p-2 sm:rounded-2xl sm:p-3" data-id="element-349">
                        8:16 • Back door opened
                      </p>
                      <p className="rounded-lg bg-white/5 p-2 sm:rounded-2xl sm:p-3" data-id="element-350">
                        8:18 • Door secured
                      </p>
                    </div>
                  </div>

                  {/* Property Status */}
                  <div className="rounded-2xl border border-white/10 bg-sky-400 p-3 text-slate-950 sm:rounded-3xl sm:p-4" data-id="element-351">
                    <div className="mb-3 flex items-center gap-1.5 text-xs font-black sm:mb-4 sm:text-sm" data-id="element-352">
                      <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-353" />
                      Status
                    </div>
                    <div className="space-y-2 sm:space-y-3" data-id="element-354">
                      {[['Perimeter', '100%'], ['Cameras', '98%'], ['Access', '84%']].map(([label, val]) => <div key={label} data-id="element-355">
                          <div className="mb-1 flex justify-between text-[10px] font-bold sm:text-xs" data-id="element-356">
                            <span data-id="element-357">{label}</span>
                            <span data-id="element-358">{val}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-950/20 sm:h-2" data-id="element-359">
                            <div className="h-1.5 rounded-full bg-slate-950 sm:h-2" style={{
                          width: val
                        }} data-id="element-360" />
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick stats strip */}
      <section className="relative z-10 -mt-12 px-4 sm:px-6 lg:px-8" data-id="element-361">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 rounded-[2rem] border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-200/70 sm:gap-4 sm:p-4 lg:grid-cols-4" data-id="element-362">
          {([[Building2, 'Commercial First', 'Built for businesses, properties, and multi site operations.', '/commercial'], [MapPinned, 'Local Service', 'Northern California walkthroughs, installs, and support.', '/about'], [ServerCog, 'Integrated Systems', 'Alarms, cameras, access control, and automation working together.', '/services'], [HomeIcon, 'Residential Ready', 'Smart home protection remains part of the full brand.', '/smart-home']] as const).map(([Icon, title, text, to]) => <Link key={title} to={to} className="group rounded-2xl bg-slate-50 p-4 transition hover:bg-slate-100 sm:rounded-3xl sm:p-6" data-id="element-363">
              <Icon className="mb-3 h-6 w-6 text-sky-500 sm:mb-4 sm:h-7 sm:w-7" data-id="element-364" />
              <h3 className="text-sm font-black sm:text-base" data-id="element-365">{title}</h3>
              <p className="mt-1.5 text-xs leading-5 text-slate-600 sm:mt-2 sm:text-sm sm:leading-6" data-id="element-366">
                {text}
              </p>
              <span className="mt-2 inline-flex items-center gap-1 text-xs font-black text-sky-600 group-hover:gap-2 sm:mt-3" data-id="element-367">
                Learn more <ArrowRight className="h-3 w-3" data-id="element-368" />
              </span>
            </Link>)}
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-white py-24" data-id="element-369">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-370">
          <div className="max-w-3xl" data-id="element-371">
            <SectionLabel data-id="element-372">Core solutions</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-373">
              One security partner for the whole property.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-374">
              Every service is connected, modern, and easy to manage. Click any
              service to see how Watchdog handles it.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-375">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.div key={service.slug} initial={{
              opacity: 0,
              y: 18
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} data-id="element-376">
                  <Link to={service.slug === 'smart-home' ? '/smart-home' : `/services/${service.slug}`} className="group block h-full rounded-[2rem] border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:bg-slate-950 hover:text-white hover:shadow-2xl" data-id="element-377">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-sky-500 shadow-sm group-hover:bg-sky-400 group-hover:text-slate-950" data-id="element-378">
                      <Icon className="h-7 w-7" data-id="element-379" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-380">{service.title}</h3>
                    <p className="mt-3 leading-7 text-slate-600 group-hover:text-slate-300" data-id="element-381">
                      {service.short}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-black text-sky-600 group-hover:text-sky-300 group-hover:gap-2" data-id="element-382">
                      Learn more <ArrowRight className="h-4 w-4" data-id="element-383" />
                    </span>
                  </Link>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Industries preview */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-id="element-384">
        <div className="mx-auto max-w-3xl text-center" data-id="element-385">
          <SectionLabel data-id="element-386">Industry ready</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-387">
            Security designed around how your property actually works.
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3" data-id="element-388">
          {industries.map(item => {
          const Icon = item.icon;
          return <Link key={item.slug} to={`/industries#${item.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl sm:rounded-3xl sm:p-6" data-id="element-389">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 group-hover:bg-sky-400 group-hover:text-slate-950 sm:mb-5 sm:h-12 sm:w-12 sm:rounded-2xl" data-id="element-390">
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-391" />
                </div>
                <h3 className="text-sm font-black sm:text-lg" data-id="element-392">{item.label}</h3>
                <p className="mt-1.5 text-xs leading-5 text-slate-600 sm:mt-2 sm:text-sm sm:leading-6" data-id="element-393">
                  {item.headline}
                </p>
              </Link>;
        })}
        </div>
        <div className="mt-10 text-center" data-id="element-394">
          <CTAButton variant="dark" to="/industries" data-id="element-395">
            See all industries
          </CTAButton>
        </div>
      </section>

      <SocialProof data-id="element-396" />

      {/* Process preview */}
      <section className="bg-slate-950 py-24 text-white" data-id="element-397">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-398">
          <div className="max-w-3xl" data-id="element-399">
            <SectionLabel dark data-id="element-400">Simple process</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-401">
              A clear path to a safer property.
            </h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-402">
            {processSteps.map((item, index) => <motion.div key={item.step} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.06
          }} className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-7" data-id="element-403">
                <div className="mb-8 text-5xl font-black text-sky-400/40" data-id="element-404">
                  {item.step}
                </div>
                <h3 className="text-xl font-black" data-id="element-405">{item.title}</h3>
                <p className="mt-4 leading-7 text-slate-300" data-id="element-406">{item.text}</p>
              </motion.div>)}
          </div>
          <div className="mt-10" data-id="element-407">
            <CTAButton to="/process" variant="secondary" data-id="element-408">
              See the full process
            </CTAButton>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-sky-50 py-20" data-id="element-409">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8" data-id="element-410">
          <SectionLabel data-id="element-411">Free assessment</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-412">
            Know what your property needs before you spend a dollar.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600" data-id="element-413">
            Tell us about your property and a Watchdog specialist will design
            and price a system tailored to it — no pressure, no scripts.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-414">
            <CTAButton to="/assessment" data-id="element-415">Get my free assessment</CTAButton>
            <CTAButton variant="dark" href="tel:+19168424729" data-id="element-416">
              Call (916) 842-4729
            </CTAButton>
          </div>
        </div>
      </section>
    </>;
}